import { useEffect, useMemo, useRef, useState } from "react";

export function useInfiniteResults<T>(items: T[], initialCount: number, step: number) {
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const [visibleCount, setVisibleCount] = useState(initialCount);

  useEffect(() => {
    setVisibleCount(Math.min(initialCount, items.length || initialCount));
  }, [initialCount, items]);

  const hasMore = visibleCount < items.length;

  useEffect(() => {
    const anchor = anchorRef.current;
    if (!anchor || !hasMore || typeof IntersectionObserver === "undefined") {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) {
          return;
        }

        setVisibleCount((current) => Math.min(current + step, items.length));
      },
      {
        rootMargin: "320px 0px",
      },
    );

    observer.observe(anchor);
    return () => {
      observer.disconnect();
    };
  }, [hasMore, items.length, step]);

  return {
    anchorRef,
    hasMore,
    visibleCount,
    visibleItems: useMemo(() => items.slice(0, visibleCount), [items, visibleCount]),
    loadMore: () => setVisibleCount((current) => Math.min(current + step, items.length)),
  };
}
